export interface TreeLeaf {
  name: string;
  description?: string;
}

export interface TreeSubItem {
  name: string;
  description?: string;
  pasal?: string[];
  children?: TreeLeaf[];
  docLink?: string;
  pnbpKategoriLink?: string;
  /** Jika true, children diambil dari /public/jenis-pnbp (DB) bukan static */
  jenisPnbpChildren?: boolean;
}

export interface TreeCategory {
  id: number;
  title: string;
  subtitle?: string;
  color:
    | "blue"
    | "indigo"
    | "violet"
    | "emerald"
    | "amber"
    | "rose"
    | "cyan"
    | "orange"
    | "red"
    | "teal";
  items: TreeSubItem[];
}

export const COLOR_MAP: Record<
  string,
  { bg: string; border: string; badge: string; icon: string; num: string; iconBg: string }
> = {
  blue:    { bg: "bg-blue-50",    border: "border-blue-200",    badge: "bg-blue-100 text-blue-700",       icon: "bg-blue-600",    num: "text-blue-600",    iconBg: "bg-blue-100" },
  indigo:  { bg: "bg-indigo-50",  border: "border-indigo-200",  badge: "bg-indigo-100 text-indigo-700",   icon: "bg-indigo-600",  num: "text-indigo-600",  iconBg: "bg-indigo-100" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  badge: "bg-violet-100 text-violet-700",   icon: "bg-violet-600",  num: "text-violet-600",  iconBg: "bg-violet-100" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-700", icon: "bg-emerald-600", num: "text-emerald-600", iconBg: "bg-emerald-100" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   badge: "bg-amber-100 text-amber-700",     icon: "bg-amber-500",   num: "text-amber-600",   iconBg: "bg-amber-100" },
  rose:    { bg: "bg-rose-50",    border: "border-rose-200",    badge: "bg-rose-100 text-rose-700",       icon: "bg-rose-600",    num: "text-rose-600",    iconBg: "bg-rose-100" },
  cyan:    { bg: "bg-cyan-50",    border: "border-cyan-200",    badge: "bg-cyan-100 text-cyan-700",       icon: "bg-cyan-600",    num: "text-cyan-600",    iconBg: "bg-cyan-100" },
  orange:  { bg: "bg-orange-50",  border: "border-orange-200",  badge: "bg-orange-100 text-orange-700",   icon: "bg-orange-500",  num: "text-orange-600",  iconBg: "bg-orange-100" },
  red:     { bg: "bg-red-50",     border: "border-red-200",     badge: "bg-red-100 text-red-700",         icon: "bg-red-600",     num: "text-red-600",     iconBg: "bg-red-100" },
  teal:    { bg: "bg-teal-50",    border: "border-teal-200",    badge: "bg-teal-100 text-teal-700",       icon: "bg-teal-600",    num: "text-teal-600",    iconBg: "bg-teal-100" },
};

export const PNBP_TREE: TreeCategory[] = [
  {
    id: 1,
    title: "Landasan & Tujuan",
    color: "blue",
    items: [
      {
        name: "Latar Belakang",
        description: "Dasar pembentukan pengaturan PNBP baru",
        pasal: ["Penjelasan Umum"],
        children: [{ name: "Penjelasan Umum UU 9/2018" }],
      },
      {
        name: "Fungsi PNBP",
        description: "Dua fungsi utama PNBP dalam sistem keuangan negara",
        pasal: ["Penjelasan Fungsi PNBP"],
        children: [
          { name: "Budgetary", description: "Fungsi penganggaran — kontribusi pendapatan negara" },
          { name: "Regulatory", description: "Fungsi pengaturan — pengendalian kekayaan negara" },
        ],
      },
      {
        name: "Tujuan Pengaturan PNBP",
        description: "Tiga tujuan utama UU PNBP",
        pasal: ["Pasal 2"],
        children: [
          { name: "Kemandirian fiskal", description: "Mengoptimalkan sumber pendapatan dan memperkuat ketahanan fiskal" },
          { name: "Kesejahteraan masyarakat", description: "Mendukung perbaikan kesejahteraan rakyat dan pertumbuhan ekonomi berkualitas" },
          { name: "Tata kelola pemerintahan", description: "Mewujudkan pelayanan yang bersih, profesional, transparan, dan akuntabel" },
        ],
      },
    ],
  },
  {
    id: 2,
    title: "Definisi & Konsep Dasar",
    color: "indigo",
    items: [
      {
        name: "Definisi-Definisi Pokok",
        description: "Pengertian istilah-istilah kunci dalam UU PNBP",
        pasal: ["Pasal 1"],
        children: [
          { name: "PNBP", description: "Pungutan yang dibayar orang pribadi atau badan atas layanan/pemanfaatan SDA dan hak negara" },
          { name: "Wajib Bayar", description: "Orang pribadi atau badan yang berkewajiban membayar PNBP" },
          { name: "Instansi Pengelola PNBP", description: "Instansi yang menyelenggarakan pengelolaan PNBP" },
          { name: "Mitra Instansi Pengelola PNBP", description: "Badan yang membantu Instansi Pengelola" },
          { name: "Pelayanan", description: "Penyediaan barang, jasa, atau pelayanan administratif oleh Pemerintah" },
          { name: "Pemanfaatan SDA", description: "Pemanfaatan bumi, air, udara, ruang angkasa, dan kekayaan alam" },
          { name: "Barang Milik Negara (BMN)", description: "Kegiatan penggunaan, pemanfaatan, dan pemindahtanganan barang negara" },
          { name: "Pengelolaan Dana", description: "Pengelolaan atas dana pemerintah untuk tujuan tertentu" },
        ],
      },
    ],
  },
  {
    id: 3,
    title: "Objek & Subjek PNBP",
    color: "violet",
    items: [
      {
        name: "Objek PNBP",
        description: "Seluruh aktivitas, hal, dan benda sumber penerimaan negara di luar perpajakan",
        pasal: ["Pasal 3", "Pasal 4"],
        docLink: "/documents/tree",
        pnbpKategoriLink: "/pnbp-objek",
        jenisPnbpChildren: true,
      },
      {
        name: "Subjek PNBP",
        description: "Pihak yang menggunakan, memperoleh manfaat, atau memiliki kaitan dengan objek PNBP",
        pasal: ["Pasal 5"],
        children: [
          { name: "Orang pribadi", description: "Dari dalam atau luar negeri" },
          { name: "Badan", description: "Dari dalam atau luar negeri" },
          { name: "→ menjadi Wajib Bayar", description: "Apabila memiliki kewajiban membayar PNBP" },
        ],
      },
    ],
  },
  {
    id: 4,
    title: "Tarif PNBP",
    color: "emerald",
    items: [
      {
        name: "Jenis Tarif",
        description: "Dua bentuk tarif atas jenis PNBP",
        pasal: ["Pasal 6"],
        children: [
          { name: "Tarif spesifik", description: "Ditetapkan dengan nilai nominal uang (contoh: Rp5.000.000/satuan)" },
          { name: "Tarif ad valorem", description: "Ditetapkan dengan persentase atau formula (contoh: 10% × dasar perhitungan)" },
        ],
      },
      {
        name: "Basis Penentuan Tarif",
        description: "Pertimbangan dalam penyusunan tarif",
        pasal: ["Pasal 7", "Pasal 8"],
        children: [
          { name: "Nilai manfaat / kadar / kualitas SDA" },
          { name: "Biaya penyelenggaraan layanan" },
          { name: "Dampak sosial, ekonomi, dan lingkungan" },
          { name: "Kebijakan Pemerintah" },
          { name: "Aspek keadilan" },
        ],
      },
      {
        name: "Kategori Tarif Berdasarkan Objek",
        description: "Ketentuan tarif per jenis objek PNBP",
        pasal: ["Pasal 7", "Pasal 8"],
        children: [
          { name: "SDA (terbarukan & tak terbarukan)", description: "→ Pasal 7: diatur dengan UU, kontrak, dan/atau PP" },
          { name: "Pelayanan (dasar & nondasar)", description: "→ Pasal 8: diatur dengan PP dan/atau Peraturan Menteri" },
          { name: "Kekayaan Negara Dipisahkan", description: "→ Pasal 9: diatur dengan UU dan/atau RUPS" },
          { name: "Barang Milik Negara", description: "→ Pasal 10: diatur dengan PP dan/atau Peraturan Menteri" },
          { name: "Pengelolaan Dana", description: "→ Pasal 11: diatur dengan Peraturan Menteri" },
          { name: "Hak Negara Lainnya", description: "→ Pasal 12: diatur dengan UU, PP, dan/atau Peraturan Menteri" },
        ],
      },
      {
        name: "Ketentuan Khusus",
        description: "Tarif dengan pertimbangan tertentu",
        pasal: ["Pasal 13"],
        children: [
          { name: "Tarif Rp0 / 0%", description: "Untuk kegiatan sosial, keagamaan, kenegaraan, kondisi kahar, masyarakat tidak mampu, mahasiswa berprestasi, dan UMKM" },
        ],
      },
    ],
  },
  {
    id: 5,
    title: "Kewenangan & Aktor",
    color: "amber",
    items: [
      {
        name: "Menteri (Pengelola Fiskal / CFO)",
        description: "Kewenangan Menteri Keuangan dalam pengelolaan PNBP",
        pasal: ["Pasal 15"],
        children: [
          { name: "Menyusun kebijakan umum Pengelolaan PNBP" },
          { name: "Mengevaluasi, menyusun, dan menetapkan jenis dan tarif PNBP" },
          { name: "Menetapkan target PNBP dan pagu penggunaan dana" },
          { name: "Menetapkan penggunaan dana PNBP" },
          { name: "Melakukan pengawasan perencanaan, pelaksanaan, dan pertanggungjawaban" },
        ],
      },
      {
        name: "Instansi Pengelola PNBP (COO)",
        description: "Kementerian/Lembaga dan Kementerian BUN",
        pasal: ["Pasal 16", "Pasal 17"],
        children: [
          { name: "Menyusun dan menyampaikan usulan jenis dan tarif PNBP" },
          { name: "Mengusulkan penggunaan dana PNBP" },
          { name: "Memungut dan menyetorkan PNBP ke Kas Negara" },
          { name: "Mengelola piutang PNBP" },
          { name: "Menyusun laporan pertanggungjawaban PNBP" },
        ],
      },
      {
        name: "Mitra Instansi Pengelola PNBP",
        description: "Badan yang membantu sebagian kegiatan pengelolaan",
        pasal: ["Pasal 19"],
        children: [
          { name: "Pemungutan PNBP" },
          { name: "Penyetoran PNBP ke Kas Negara" },
          { name: "Pelaporan kepada Instansi Pengelola" },
          { name: "Penatausahaan PNBP" },
        ],
      },
    ],
  },
  {
    id: 6,
    title: "Siklus Pengelolaan PNBP",
    color: "cyan",
    items: [
      {
        name: "Perencanaan",
        description: "Penyusunan rencana PNBP dalam siklus APBN",
        pasal: ["Pasal 22", "Pasal 23"],
        children: [
          { name: "Target PNBP", description: "Disusun secara realistis, optimal, dan sesuai ketentuan" },
          { name: "Pagu Penggunaan Dana PNBP", description: "Untuk Instansi yang telah mendapat persetujuan penggunaan dana" },
        ],
      },
      {
        name: "Pelaksanaan",
        description: "Tahap operasional pengelolaan PNBP",
        pasal: ["Pasal 26", "Pasal 28", "Pasal 29", "Pasal 31", "Pasal 33", "Pasal 35"],
        children: [
          { name: "Penetapan PNBP Terutang", description: "→ Pasal 26: dihitung oleh Instansi, Mitra, atau Wajib Bayar" },
          { name: "Pemungutan PNBP", description: "→ Pasal 28: wajib dilaksanakan Instansi Pengelola" },
          { name: "Pembayaran PNBP Terutang", description: "→ Pasal 31: denda 2%/bulan jika terlambat (maks. 24 bulan)" },
          { name: "Penyetoran ke Kas Negara", description: "→ Pasal 29: seluruh PNBP wajib disetor ke Kas Negara" },
          { name: "Penggunaan Dana PNBP", description: "→ Pasal 33: atas persetujuan Menteri" },
          { name: "Pengelolaan Piutang PNBP", description: "→ Pasal 35: dicatat jika Wajib Bayar belum membayar" },
        ],
      },
      {
        name: "Pertanggungjawaban",
        description: "Pencatatan dan pelaporan realisasi PNBP",
        pasal: ["Pasal 41", "Pasal 42", "Pasal 43"],
        children: [
          { name: "Penatausahaan", description: "→ Pasal 41: wajib oleh Instansi dan Wajib Bayar; dokumen disimpan 10 tahun" },
          { name: "Pelaporan Wajib Bayar", description: "→ Pasal 42: paling lambat 20 hari kalender; denda Rp1 juta jika terlambat" },
          { name: "Pelaporan Instansi Pengelola", description: "→ Pasal 43: laporan realisasi penerimaan dan penggunaan dana kepada Menteri" },
        ],
      },
      {
        name: "Pengawasan",
        description: "Pengawasan intern dan oleh Pemerintah Pusat",
        pasal: ["Pasal 45", "Pasal 46"],
        children: [
          { name: "Pengawasan Intern", description: "→ Pasal 45: oleh APIP yang bertanggung jawab kepada Menteri/Pimpinan Lembaga" },
          { name: "Pengawasan Menteri", description: "→ Pasal 46: verifikasi, penilaian, dan/atau evaluasi terhadap Instansi Pengelola" },
        ],
      },
    ],
  },
  {
    id: 7,
    title: "Pengendalian & Pemeriksaan",
    color: "teal",
    items: [
      {
        name: "Dasar & Lingkup Pemeriksaan PNBP",
        description: "Pemeriksaan oleh instansi pemeriksa (BPKP)",
        pasal: ["Pasal 47"],
        children: [
          { name: "Pemeriksaan terhadap Wajib Bayar", description: "→ Pasal 47–48: atas permintaan Pimpinan Instansi atau Menteri" },
          { name: "Pemeriksaan terhadap Instansi Pengelola", description: "→ Pasal 50: atas permintaan Menteri" },
          { name: "Pemeriksaan terhadap Mitra", description: "→ Pasal 51: atas permintaan Menteri dan/atau Pimpinan Instansi" },
        ],
      },
      {
        name: "Ruang Lingkup Pemeriksaan",
        description: "Cakupan objek pemeriksaan",
        pasal: ["Pasal 52"],
        children: [
          { name: "Laporan keuangan dan dokumen pendukung" },
          { name: "Bukti transaksi keuangan pembayaran/penyetoran" },
          { name: "Sistem pengendalian intern terkait pengelolaan PNBP" },
        ],
      },
      {
        name: "Output Pemeriksaan & Penetapan",
        description: "Surat-surat yang diterbitkan berdasarkan hasil pemeriksaan",
        pasal: ["Pasal 36", "Pasal 37", "Pasal 38"],
        children: [
          { name: "Surat Tagihan PNBP", description: "→ Pasal 37: untuk tagihan pokok maupun sanksi administratif denda" },
          { name: "Surat Ketetapan PNBP (Kurang/Nihil/Lebih Bayar)", description: "→ Pasal 36: penetapan PNBP Terutang; masa daluwarsa 10 tahun" },
          { name: "Koreksi Surat Tagihan", description: "→ Pasal 38: Wajib Bayar dapat mengajukan permohonan koreksi secara tertulis" },
        ],
      },
    ],
  },
  {
    id: 8,
    title: "Hak Wajib Bayar",
    color: "orange",
    items: [
      {
        name: "Keberatan",
        description: "Hak mengajukan keberatan atas Surat Ketetapan PNBP",
        pasal: ["Pasal 58", "Pasal 59", "Pasal 60"],
        children: [
          { name: "Pengajuan keberatan secara tertulis", description: "Dalam jangka waktu 3 bulan sejak tanggal SK PNBP" },
          { name: "Penetapan dalam 6 bulan", description: "Jika tidak ada penetapan, keberatan dianggap dikabulkan" },
          { name: "Penetapan bersifat final", description: "Dapat digugat ke Pengadilan Tinggi TUN jika tidak setuju" },
        ],
      },
      {
        name: "Keringanan PNBP",
        description: "Permohonan keringanan atas PNBP Terutang",
        pasal: ["Pasal 62"],
        children: [
          { name: "Penundaan", description: "Atas kondisi di luar kemampuan / kahar / kesulitan likuiditas" },
          { name: "Pengangsuran", description: "Pembayaran secara cicilan" },
          { name: "Pengurangan", description: "Perlu persetujuan Menteri" },
          { name: "Pembebasan", description: "Perlu persetujuan Menteri dan pertimbangan APIP / rekomendasi pemeriksa" },
        ],
      },
      {
        name: "Pengembalian (Refund)",
        description: "Pengembalian atas kelebihan pembayaran PNBP",
        pasal: ["Pasal 63"],
        children: [
          { name: "Kompensasi (pembayaran di muka)", description: "Default: diperhitungkan sebagai pembayaran berikutnya" },
          { name: "Pengembalian langsung (tunai)", description: "Dalam kondisi tertentu: pengakhiran usaha, putusan pengadilan, dll." },
          { name: "Batas waktu permohonan", description: "5 tahun (umum) / 2 tahun (putusan pengadilan atau laporan pemeriksaan)" },
        ],
      },
    ],
  },
  {
    id: 9,
    title: "Sanksi & Pidana",
    color: "rose",
    items: [
      {
        name: "Sanksi Administratif terhadap Wajib Bayar",
        description: "Denda atas pelanggaran kewajiban pembayaran dan pelaporan",
        pasal: ["Pasal 31", "Pasal 41", "Pasal 42"],
        children: [
          { name: "Denda keterlambatan pembayaran", description: "→ Pasal 31: 2%/bulan dari PNBP Terutang, maks. 24 bulan" },
          { name: "Denda tidak menatausahakan", description: "→ Pasal 41: Rp10.000.000" },
          { name: "Denda tidak menyampaikan laporan", description: "→ Pasal 42: Rp1.000.000" },
        ],
      },
      {
        name: "Sanksi terhadap Instansi & Mitra",
        description: "Sanksi atas pelanggaran kewajiban pemungutan dan penyetoran",
        pasal: ["Pasal 28"],
        children: [
          { name: "Tidak melakukan verifikasi", description: "→ Pasal 27: sanksi sesuai ketentuan perundang-undangan" },
          { name: "Tidak melaksanakan pemungutan", description: "→ Pasal 28: sanksi sesuai ketentuan perundang-undangan" },
          { name: "Tidak mengelola piutang PNBP", description: "→ Pasal 35: sanksi sesuai ketentuan perundang-undangan" },
        ],
      },
      {
        name: "Sanksi Pidana",
        description: "Ketentuan pidana atas tindak pidana PNBP",
        pasal: ["Pasal 67", "Pasal 68"],
        children: [
          { name: "Wajib Bayar sengaja tidak membayar/lapor", description: "→ Pasal 67: denda 4× PNBP Terutang + penjara 2–6 tahun" },
          { name: "Sengaja tidak memberikan dokumen/keterangan", description: "→ Pasal 68: denda maks. Rp1 miliar atau kurungan maks. 1 tahun" },
        ],
      },
    ],
  },
  {
    id: 10,
    title: "Implementasi Operasional",
    subtitle: "PP 44/2025",
    color: "teal",
    items: [
      {
        name: "Penetapan Tarif",
        description: "Proses penetapan tarif PNBP secara operasional",
        pasal: ["Pasal 18 PP 44/2025", "Pasal 19 PP 44/2025", "Pasal 20 PP 44/2025"],
        children: [
          { name: "Usulan tarif oleh Instansi Pengelola", description: "→ Pasal 19 PP 44/2025: termasuk analisis efektivitas, kinerja, latar belakang, dasar perhitungan, dan dampak" },
          { name: "Evaluasi tarif oleh Menteri", description: "→ Pasal 20 PP 44/2025: hasil evaluasi dapat berupa penyesuaian dan/atau penyederhanaan tarif" },
          { name: "Persetujuan Menteri selaku pengelola fiskal", description: "→ Pasal 18 PP 44/2025: wajib sebelum Kementerian/Lembaga menetapkan tarif via Peraturan Menteri" },
        ],
      },
      {
        name: "Pengelolaan Mitra",
        description: "Ketentuan operasional Mitra Instansi Pengelola",
        pasal: ["Pasal 5 PP 44/2025", "Pasal 6 PP 44/2025", "Pasal 7 PP 44/2025", "Pasal 8 PP 44/2025"],
        children: [
          { name: "Penunjukan Mitra berdasarkan UU atau penugasan", description: "→ Pasal 5 PP 44/2025: wajib mendapat persetujuan Menteri sebelum penugasan" },
          { name: "Tugas Mitra: pemungutan, penyetoran, penagihan", description: "→ Pasal 6 PP 44/2025: termasuk penentuan PNBP Terutang dan monitoring" },
          { name: "Imbal jasa Mitra", description: "→ Pasal 7 PP 44/2025: berupa pembagian pendapatan dan/atau dukungan pendanaan APBN" },
          { name: "Sanksi administratif terhadap Mitra", description: "→ Pasal 8 PP 44/2025: berjenjang dari teguran tertulis hingga pencabutan status" },
        ],
      },
      {
        name: "Tata Kelola Teknis",
        description: "Mekanisme kontrak dan pengawasan teknis",
        pasal: ["Pasal 17 PP 44/2025", "Pasal 57 PP 44/2025"],
        children: [
          { name: "Kontrak antara Instansi dan Mitra", description: "→ Pasal 17 PP 44/2025: tarif tidak dapat ditentukan di awal atau disepakati bersama Wajib Bayar" },
          { name: "Pengawasan pemenuhan kewajiban PNBP", description: "→ Pasal 57 PP 44/2025: pengawasan dilakukan terhadap pemenuhan kewajiban dan kepatuhan perundang-undangan" },
          { name: "Prosedur operasional standar dan pelaporan", description: "Diatur lebih lanjut dalam Peraturan Menteri" },
        ],
      },
    ],
  },
];
